// =============================================================================
// glLinesExcel.js
// Excel template export / import for GL budget lines (bud_gl_lines).
//
// Export: builds an .xlsx with an entry sheet whose Account / Cost Centre /
//         Site / Calculated / Source Module columns carry in-cell dropdowns
//         (data validation) backed by a hidden "Lists" sheet of master data —
//         the same choices offered in the Add Records grid. Existing lines for
//         the version are pre-filled so the file doubles as an "edit & re-import"
//         template.
//
// Import: parses a completed template, resolves each cell back to its code,
//         validates against the supplied reference data, and returns
//         { lines, errors } ready for budgetSetupService.addGLLines.
//
// `ref` is the reference-data payload: { accounts, costCenters, sites, modules }.
// =============================================================================
import ExcelJS from 'exceljs';

const SHEET = 'GL Budget Lines';
const DELIM = ' — ';            // separates "CODE — Description" in dropdown values
const ALL_SITES = '(All sites)'; // explicit "no site" choice
const ALL_ROLES = '(All roles)'; // explicit "no role" choice — stored as NULL
const BLANK_ROWS = 250;          // empty rows (with dropdowns) appended after the data

// "5000 — Rent Expense" -> "5000"; plain "5000" -> "5000".
const codeOf = (raw) => {
  if (raw == null) return '';
  const s = String(raw).trim();
  const i = s.indexOf(DELIM);
  return (i >= 0 ? s.slice(0, i) : s).trim();
};

const labelMap = (rows, codeKey, descKey) =>
  Object.fromEntries(rows.map((r) => [r[codeKey], `${r[codeKey]}${DELIM}${r[descKey]}`]));

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const safeName = (s) => String(s || 'version').replace(/[^a-z0-9_-]+/gi, '_');

// ---- Export -----------------------------------------------------------------
export async function exportGLTemplate({ versionLabel, ref, existingLines = [], blankRows = BLANK_ROWS }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mantle';
  wb.created = new Date();

  const ws = wb.addWorksheet(SHEET, { views: [{ state: 'frozen', ySplit: 1 }] });
  const lists = wb.addWorksheet('Lists');
  lists.state = 'veryHidden';

  // Dropdown option arrays (displayed as "CODE — Description").
  const accOpts = ref.accounts.map((a) => `${a.account_code}${DELIM}${a.account_description}`);
  const ccOpts = ref.costCenters.map((c) => `${c.cost_center_code}${DELIM}${c.description}`);
  const siteOpts = [ALL_SITES, ...ref.sites.map((s) => `${s.site_code}${DELIM}${s.site_name}`)];
  const modOpts = ref.modules.map((m) => `${m.module_code}${DELIM}${m.module_name}`);
  // Roles are keyed by urid (an integer), so the code half is the urid.
  const roleOpts = [ALL_ROLES, ...(ref.roles || []).map((r) => `${r.urid}${DELIM}${r.ur_name}`)];
  const yesNo = ['Yes', 'No'];

  // Stash each option list in its own column on the hidden sheet (data from row 2).
  const putList = (col, header, arr) => {
    lists.getCell(`${col}1`).value = header;
    arr.forEach((v, i) => { lists.getCell(`${col}${i + 2}`).value = v; });
  };
  putList('A', 'Accounts', accOpts);
  putList('B', 'CostCentres', ccOpts);
  putList('C', 'Sites', siteOpts);
  putList('D', 'Modules', modOpts);
  putList('E', 'YesNo', yesNo);
  putList('F', 'Roles', roleOpts);

  // Entry-sheet columns + header styling (mirrors the app's black grid header).
  ws.columns = [
    { header: 'Account', key: 'acc', width: 40 },
    { header: 'Cost Centre', key: 'cc', width: 36 },
    { header: 'Site', key: 'site', width: 30 },
    { header: 'Role', key: 'role', width: 28 },
    { header: 'Calculated', key: 'calc', width: 14 },
    { header: 'Source Module', key: 'mod', width: 30 },
  ];
  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFF8FAFC' } };
  headerRow.alignment = { vertical: 'middle' };
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } };
  });

  // Pre-fill existing configured lines so the file can be edited & re-imported.
  const accLabel = labelMap(ref.accounts, 'account_code', 'account_description');
  const ccLabel = labelMap(ref.costCenters, 'cost_center_code', 'description');
  const siteLabel = labelMap(ref.sites, 'site_code', 'site_name');
  const modLabel = labelMap(ref.modules, 'module_code', 'module_name');
  const roleLabel = labelMap(ref.roles || [], 'urid', 'ur_name');

  existingLines.forEach((l, i) => {
    const row = ws.getRow(i + 2);
    row.getCell(1).value = accLabel[l.account_code] || l.account_code;
    row.getCell(2).value = ccLabel[l.cost_center_code] || l.cost_center_code;
    row.getCell(3).value = l.site_code ? (siteLabel[l.site_code] || l.site_code) : ALL_SITES;
    row.getCell(4).value = l.role_id != null ? (roleLabel[l.role_id] || String(l.role_id)) : ALL_ROLES;
    row.getCell(5).value = l.is_calculated ? 'Yes' : 'No';
    row.getCell(6).value = l.is_calculated && l.source_module
      ? (modLabel[l.source_module] || l.source_module)
      : '';
  });

  // Attach dropdowns to every entry row (existing + blank) for easy editing.
  const listRef = (col, len) => [`=Lists!$${col}$2:$${col}$${len + 1}`];
  const validation = (formulae, prompt) => ({
    type: 'list',
    allowBlank: true,
    formulae,
    showErrorMessage: true,
    errorStyle: 'warning',
    error: prompt,
    errorTitle: 'Pick from the list',
  });

  // Dynamic range: one validated row per existing line + a buffer of blank
  // rows for new entries (+1 because row 1 is the header).
  const lastRow = existingLines.length + blankRows + 1;
  for (let r = 2; r <= lastRow; r++) {
    ws.getCell(`A${r}`).dataValidation = validation(listRef('A', accOpts.length), 'Choose an account.');
    ws.getCell(`B${r}`).dataValidation = validation(listRef('B', ccOpts.length), 'Choose a cost centre.');
    ws.getCell(`C${r}`).dataValidation = validation(listRef('C', siteOpts.length), 'Choose a site or "(All sites)".');
    ws.getCell(`D${r}`).dataValidation = validation(listRef('F', roleOpts.length), 'Choose a role or "(All roles)".');
    ws.getCell(`E${r}`).dataValidation = validation(listRef('E', yesNo.length), 'Yes or No.');
    ws.getCell(`F${r}`).dataValidation = validation(listRef('D', modOpts.length), 'Choose a source module.');
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `GL_Lines_Template_${safeName(versionLabel)}.xlsx`);
}

// ---- Import -----------------------------------------------------------------
// Returns { lines, errors }. `lines` is empty when there are any errors so the
// caller can refuse a partial import.
export async function parseGLTemplate(file, ref) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.getWorksheet(SHEET) || wb.worksheets[0];
  if (!ws) return { lines: [], errors: ['Could not find a worksheet in the file.'] };

  const accSet = new Set(ref.accounts.map((a) => a.account_code));
  const ccSet = new Set(ref.costCenters.map((c) => c.cost_center_code));
  const siteSet = new Set(ref.sites.map((s) => s.site_code));
  const modSet = new Set(ref.modules.map((m) => m.module_code));
  const roleSet = new Set((ref.roles || []).map((r) => String(r.urid)));

  // ExcelJS cell.value may be a string, number, or rich-text/formula object.
  const cellText = (cell) => {
    const v = cell?.value;
    if (v == null) return '';
    if (typeof v === 'object') return String(v.text ?? v.result ?? '');
    return String(v);
  };

  const lines = [];
  const errors = [];
  const seen = new Set();

  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header

    const accRaw = cellText(row.getCell(1));
    const ccRaw = cellText(row.getCell(2));
    const siteRaw = cellText(row.getCell(3));
    const roleRaw = cellText(row.getCell(4));
    const calcRaw = cellText(row.getCell(5));
    const modRaw = cellText(row.getCell(6));

    // Skip entirely blank rows.
    if (![accRaw, ccRaw, siteRaw, roleRaw, calcRaw, modRaw].some((s) => s.trim())) return;

    const rowErrs = [];
    const account_code = codeOf(accRaw);
    const cost_center_code = codeOf(ccRaw);

    if (!account_code) rowErrs.push('missing account');
    else if (!accSet.has(account_code)) rowErrs.push(`unknown account "${account_code}"`);

    if (!cost_center_code) rowErrs.push('missing cost centre');
    else if (!ccSet.has(cost_center_code)) rowErrs.push(`unknown cost centre "${cost_center_code}"`);

    let site_code = null;
    const siteTrim = siteRaw.trim();
    if (siteTrim && siteTrim !== ALL_SITES) {
      const sc = codeOf(siteRaw);
      if (!siteSet.has(sc)) rowErrs.push(`unknown site "${sc}"`);
      else site_code = sc;
    }

    // Role: blank or "(All roles)" => NULL => visible to every role.
    let role_id = null;
    const roleTrim = roleRaw.trim();
    if (roleTrim && roleTrim !== ALL_ROLES) {
      const rc = codeOf(roleRaw);
      if (!roleSet.has(rc)) rowErrs.push(`unknown role "${rc}"`);
      else role_id = Number(rc);
    }

    const is_calculated = /^(yes|true|y|1)$/i.test(calcRaw.trim());

    let source_module = null;
    if (is_calculated && modRaw.trim()) {
      const mc = codeOf(modRaw);
      if (!modSet.has(mc)) rowErrs.push(`unknown module "${mc}"`);
      else source_module = mc;
    }

    if (rowErrs.length === 0) {
      const key = `${account_code}|${cost_center_code}|${site_code || ''}`;
      if (seen.has(key)) rowErrs.push('duplicate of an earlier row');
      else seen.add(key);
    }

    if (rowErrs.length) errors.push(`Row ${rowNumber}: ${rowErrs.join(', ')}`);
    else lines.push({ account_code, cost_center_code, site_code, role_id, is_calculated, source_module });
  });

  if (errors.length) return { lines: [], errors };
  return { lines, errors: [] };
}

export default { exportGLTemplate, parseGLTemplate };
